"use client"
import React, {createContext, useContext, useMemo} from "react";
import {useAppSelector} from "@/store/hooks";
import {ProjectTheme} from "@/services/models/project-theme";

interface PrThemeProviderProps {
    children: React.ReactNode;
    /**
     * Optional overrides for widget-specific theming
     * Used by: Widget-ISA (green), Widget-BO (yellow)
     * DEFAULT: undefined (existing behavior preserved)
     */
    overrides?: Partial<PrThemeContextType>;
}

export interface PrThemeContextType {
    primary_color: string;
    accent_color: string;
    secondary_color: string;
    tertiary_color: string;
    isDefault?: boolean;
}

const PrThemeContext = createContext<PrThemeContextType | undefined>(undefined)

function returnThemeOrDefault(theme: ProjectTheme | PrThemeContextType | undefined = undefined) {
    if (theme) {
        // Fix for themes with incorrect tertiary_color (purple instead of blue)
        // Check if this is the "eon default" theme or has the old purple color
        const hasOldPurpleColor = theme.tertiary_color?.toLowerCase() === 'd7cdf9';
        const isEonDefault = 'display_name' in theme && theme.display_name?.toLowerCase().includes('eon default');
        
        if (hasOldPurpleColor || isEonDefault) {
            return {
                ...theme,
                tertiary_color: "0027F9", // Fix: Use PayRetailers blue
                isDefault: false
            };
        }
        
        return theme
    }
    return {
        primary_color: "ffffff",
        accent_color: "ffe8a8",
        secondary_color: "d6d6ff",
        tertiary_color: "0027F9", // PayRetailers blue
        isDefault: true
    }
}

export const usePrTheme: () => PrThemeContextType = () => {
    const context = useContext(PrThemeContext)
    if (!context) {
        // Fallback if used outside provider (backwards compatibility)
        return returnThemeOrDefault(undefined);
    }
    return context;
}

export function PrThemeProvider({children, overrides}: PrThemeProviderProps) {
    const reduxTheme = useAppSelector(state => state.theme.currentTheme)
    
    // Merge redux theme with overrides (new feature)
    // If no overrides, behaves exactly as before
    const finalTheme = useMemo(() => {
        const base = returnThemeOrDefault(reduxTheme);
        if (overrides) {
            return {
                ...base,
                ...overrides,
                isDefault: false // Overrides mean custom theme
            };
        }
        return base;
    }, [reduxTheme, overrides]);
    
    return (
        <PrThemeContext.Provider value={finalTheme}>
            {children}
        </PrThemeContext.Provider>
    )
}
